import { useState, useEffect } from 'react';
import {
  DocumentoListItem,
  listarDocumentos,
  abrirOuBaixarDocumento,
  isInlineFormat,
} from '../../services/documentoService';
import './ClienteDocumentosView.css';

interface ClienteDocumentosViewProps {
  projetoId: string;
}

/**
 * Visualização somente leitura de documentos do projeto para o Cliente.
 * Sem botões de upload/exclusão. Permite download e visualização inline (JPEG/PNG/PDF).
 * Req 4.1, 4.2, 4.3, 4.4, 4.5, 4.6, 4.7, 4.8
 */
function ClienteDocumentosView({ projetoId }: ClienteDocumentosViewProps) {
  const [documentos, setDocumentos] = useState<DocumentoListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadError, setDownloadError] = useState('');

  useEffect(() => {
    async function fetchDocumentos() {
      try {
        setLoading(true);
        setError('');
        const data = await listarDocumentos(projetoId);
        setDocumentos(data);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar documentos.');
      } finally {
        setLoading(false);
      }
    }

    if (projetoId) {
      fetchDocumentos();
    }
  }, [projetoId]);

  /**
   * Formata data ISO para dd/mm/aaaa HH:mm.
   */
  function formatarData(dataStr: string): string {
    const date = new Date(dataStr);
    const dia = String(date.getDate()).padStart(2, '0');
    const mes = String(date.getMonth() + 1).padStart(2, '0');
    const ano = date.getFullYear();
    const horas = String(date.getHours()).padStart(2, '0');
    const minutos = String(date.getMinutes()).padStart(2, '0');
    return `${dia}/${mes}/${ano} ${horas}:${minutos}`;
  }

  /**
   * Trunca o nome do arquivo para no máximo 100 caracteres.
   */
  function truncarNome(nome: string, maxChars = 100): string {
    if (nome.length <= maxChars) return nome;
    return nome.substring(0, maxChars - 3) + '...';
  }

  /**
   * Lida com o click no nome do documento ou botão de visualização.
   * Para JPEG/PNG/PDF: abre em nova aba (inline).
   * Para outros: inicia download direto.
   */
  async function handleVisualizarOuBaixar(doc: DocumentoListItem) {
    try {
      setDownloadError('');
      await abrirOuBaixarDocumento(projetoId, doc.id, doc.formato, doc.nomeOriginal);
    } catch {
      setDownloadError('Não foi possível recuperar o arquivo.');
    }
  }

  /**
   * Lida com o botão de download (sempre força download com nome original).
   */
  async function handleDownload(doc: DocumentoListItem) {
    try {
      setDownloadError('');
      const token = localStorage.getItem('auth_token');
      const url = `/api/empreendimentos/${projetoId}/documentos/${doc.id}/download`;

      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!res.ok) {
        throw new Error('Não foi possível recuperar o arquivo.');
      }

      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = doc.nomeOriginal;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      setDownloadError('Não foi possível recuperar o arquivo.');
    }
  }

  if (loading) {
    return (
      <div className="cliente-documentos">
        <p className="cliente-documentos__loading">Carregando documentos...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cliente-documentos">
        <p className="cliente-documentos__error">{error}</p>
      </div>
    );
  }

  return (
    <div className="cliente-documentos">
      <h2 className="cliente-documentos__title">Documentos do Projeto</h2>

      {downloadError && (
        <div className="cliente-documentos__download-error" role="alert">
          {downloadError}
          <button
            className="cliente-documentos__dismiss-error"
            onClick={() => setDownloadError('')}
            aria-label="Fechar mensagem de erro"
          >
            ✕
          </button>
        </div>
      )}

      {documentos.length === 0 ? (
        <div className="cliente-documentos__empty">
          <span className="cliente-documentos__empty-icon" aria-hidden="true">📄</span>
          <p>Não há documentos no repositório deste projeto.</p>
        </div>
      ) : (
        <div className="cliente-documentos__list" role="list">
          <div className="cliente-documentos__header">
            <span className="cliente-documentos__col cliente-documentos__col--nome">Arquivo</span>
            <span className="cliente-documentos__col cliente-documentos__col--descricao">Descrição</span>
            <span className="cliente-documentos__col cliente-documentos__col--data">Data</span>
            <span className="cliente-documentos__col cliente-documentos__col--enviado">Enviado por</span>
            <span className="cliente-documentos__col cliente-documentos__col--acoes">Ações</span>
          </div>

          {documentos.map((doc) => (
            <div className="cliente-documentos__item" key={doc.id} role="listitem">
              <span className="cliente-documentos__col cliente-documentos__col--nome">
                <button
                  className="cliente-documentos__nome-link"
                  onClick={() => handleVisualizarOuBaixar(doc)}
                  title={doc.nomeOriginal}
                  aria-label={
                    isInlineFormat(doc.formato)
                      ? `Visualizar ${doc.nomeOriginal}`
                      : `Baixar ${doc.nomeOriginal}`
                  }
                >
                  {truncarNome(doc.nomeOriginal)}
                </button>
                <span className="cliente-documentos__formato">{doc.formato.toUpperCase()}</span>
              </span>
              <span className="cliente-documentos__col cliente-documentos__col--descricao">
                {doc.descricao}
              </span>
              <span className="cliente-documentos__col cliente-documentos__col--data">
                {formatarData(doc.enviadoEm)}
              </span>
              <span className="cliente-documentos__col cliente-documentos__col--enviado">
                {doc.enviadoPor.nome}
              </span>
              <span className="cliente-documentos__col cliente-documentos__col--acoes">
                <button
                  className="cliente-documentos__btn-download"
                  onClick={() => handleDownload(doc)}
                  aria-label={`Baixar ${doc.nomeOriginal}`}
                  title="Download"
                >
                  ⬇ Baixar
                </button>
                {isInlineFormat(doc.formato) && (
                  <button
                    className="cliente-documentos__btn-visualizar"
                    onClick={() => handleVisualizarOuBaixar(doc)}
                    aria-label={`Visualizar ${doc.nomeOriginal} em nova aba`}
                    title="Visualizar"
                  >
                    👁 Ver
                  </button>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ClienteDocumentosView;
